import { Classes, MenuItemProps } from "@blueprintjs/core";
import { PlatformRole } from "@hex/common";
import React from "react";
import styled from "styled-components";

import {
  HexButton,
  HexMenu,
  HexMenuDivider,
  HexMenuItem,
  HexPopover,
} from "../../hex-components";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { useOnClickProps } from "../../hooks/useOnClickProps";
import { Routes } from "../../route/routes";
import { TestSelectors } from "../../util/testSelectors";
import { trackCounterEvent } from "../../util/trackEvent";
import { WorkspaceDropdownCreditMeter } from "../ai-monetization/WorkspaceDropdownCreditMeter";
import { QuickSettings } from "../app/QuickSettings";
import { AdminMenuItems } from "../common/AdminMenuItems";
import { AuthMenuItems } from "../common/AuthMenuItems";
import {
  ConfigureIcon,
  PreferencesIcon,
  SingleChevronDownIcon,
} from "../icons/CustomIcons";

const PreferencePopoverProps: MenuItemProps["popoverProps"] = {
  usePortal: true,
};

const NavMenu = styled(HexMenu)`
  &&.${Classes.MENU} {
    min-width: 220px;
    max-width: 260px;
    max-height: 80vh;

    box-shadow: none;
  }
`;
export const WorkspaceDropdownPicker = React.memo(
  function WorkspaceDropdownPicker({
    className,
    hideSettings,
    location,
    showUserPreferences,
  }: {
    className?: string;
    hideSettings?: boolean;
    showUserPreferences?: boolean;
    location?: "popover_sidebar" | "home_sidebar";
  }) {
    const currentUser = useCurrentUser();
    const currentOrg = currentUser?.org;
    const settingsMenuItemOnClickProps = useOnClickProps({
      to: Routes.SETTINGS.getUrl({}),
      onClick: () =>
        location &&
        trackCounterEvent(`${location}.workspace_menu.settings.click`),
    });
    const preferencesMenuItemOnClickProps = useOnClickProps({
      to: Routes.SETTINGS.getUrl({ subView: "preferences" }),
      onClick: () =>
        location &&
        trackCounterEvent(`${location}.user_menu.preferences.click`),
    });

    return (
      <HexPopover
        content={
          <NavMenu>
            <WorkspaceDropdownCreditMeter />
            {!hideSettings && (
              <>
                <HexMenuItem
                  icon={<ConfigureIcon />}
                  text="Settings"
                  {...settingsMenuItemOnClickProps}
                />
                <HexMenuDivider />
              </>
            )}
            {showUserPreferences && (
              <>
                <HexMenuItem
                  icon={<PreferencesIcon />}
                  popoverProps={PreferencePopoverProps}
                  text="Preferences"
                >
                  <QuickSettings flatten={true} location={location} />
                  <HexMenuDivider />
                  <HexMenuItem
                    text="All preferences"
                    {...preferencesMenuItemOnClickProps}
                  />
                </HexMenuItem>
                <HexMenuDivider />
              </>
            )}
            {(currentUser?.platformRole === PlatformRole.ADMIN ||
              currentUser?.platformRole === PlatformRole.ADMIN_READ_ONLY) && (
              <>
                <AdminMenuItems currentUser={currentUser} />
                <HexMenuDivider />
              </>
            )}
            <AuthMenuItems currentOrg={currentOrg} />
          </NavMenu>
        }
        minimal={true}
        placement="bottom-start"
        scrollContent={true}
        usePortal={false}
      >
        <OrgNameDropdown
          className={className}
          data-test={TestSelectors.ORG_NAME_DROPDOWN}
          minimal={true}
          rightIcon={<SingleChevronDownIcon />}
        >
          {currentOrg?.displayName}
        </OrgNameDropdown>
      </HexPopover>
    );
  },
);

const OrgNameDropdown = styled(HexButton)`
  && {
    overflow: hidden;
    text-overflow: ellipsis;

    font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};

    &&&&&&&.${Classes.BUTTON_TEXT} {
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 17px;
    }
  }
`;
